/**
 * ComplianceCheckDetail — compilazione/lettura di un controllo di conformità:
 * punteggi 1-5 per voce, referente, note e piano d'azione, firme
 * Valutatore + Referente, PDF e finalizzazione.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { complianceApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { CheckCheck, FileDown, Loader2, MessageSquarePlus, PenLine, Save, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../SignaturePad';

const SigBlock = ({ sig, label, onSign, testId }) => (
  <div className="flex items-center gap-2" data-testid={testId}>
    {sig ? (
      <>
        <img src={sig.image} alt={label} className="h-10 max-w-[140px] object-contain border border-slate-200 bg-white px-2" />
        <div className="text-[11px] text-slate-500">
          <p className="font-semibold text-slate-700">{label}</p>
          <p>{sig.name}</p>
        </div>
      </>
    ) : (
      <Button variant="outline" size="sm" className="h-8 gap-1.5 rounded-none" onClick={onSign}>
        <PenLine className="w-3.5 h-3.5" /> {label}
      </Button>
    )}
  </div>
);

export default function ComplianceCheckDetail({ checkId, storeName, onClose }) {
  const { user } = useAuth();
  const [chk, setChk] = useState(null);
  const [scores, setScores] = useState({});
  const [referente, setReferente] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [signCtx, setSignCtx] = useState(null);
  const [comment, setComment] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);

  const load = () => {
    complianceApi.check(checkId).then(r => {
      setChk(r.data);
      setScores(r.data.scores || {});
      setReferente(r.data.referente_name || '');
      setDate(r.data.date || '');
      setNotes(r.data.notes || '');
      setDirty(false);
    }).catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento'); onClose(); });
  };
  useEffect(load, [checkId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosalvataggio: come le schede di valutazione, ogni modifica viene
  // persistita da sola dopo 1.2s — uscire e rientrare non perde nulla.
  useEffect(() => {
    if (!dirty || !chk || chk.status === 'final') return undefined;
    const timer = setTimeout(() => { save(); }, 1200);
    return () => clearTimeout(timer);
  }, [dirty, scores, referente, date, notes]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!chk) return null;

  const tpl = chk.template || {};
  const scaleMin = tpl.scale_min || 1;
  const scaleMax = tpl.scale_max || 5;
  const isFinal = chk.status === 'final';
  const canEdit = !isFinal;
  const allItems = (tpl.sections || []).flatMap(s => s.items);
  const total = allItems.reduce((a, it) => a + (scores[it.item_id] || 0), 0);
  const maxTotal = allItems.length * scaleMax;
  const scored = allItems.filter(it => Number.isInteger(scores[it.item_id])).length;
  const percent = maxTotal ? Math.round((total / maxTotal) * 1000) / 10 : 0;
  const secTotal = (sec) => sec.items.reduce((a, it) => a + (scores[it.item_id] || 0), 0);

  const setScore = (itemId, v) => {
    if (!canEdit) return;
    setScores(s => ({ ...s, [itemId]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await complianceApi.updateCheck(checkId, {
        scores, referente_name: referente || null, date: date || null, notes: notes || null,
      });
      setChk(c => ({ ...c, ...r.data }));
      setDirty(false);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (scored < allItems.length && !window.confirm(`Hai valutato ${scored} voci su ${allItems.length}. Finalizzare comunque?`)) return;
    if (!window.confirm('Rendere definitivo il controllo? Punteggi e note non saranno più modificabili (potrai comunque aggiungere aggiornamenti).')) return;
    try {
      if (dirty && !(await save())) return;
      await complianceApi.finalizeCheck(checkId);
      toast.success('Controllo definitivo');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Operazione fallita'); }
  };

  const doSign = async (b64) => {
    try {
      await complianceApi.signCheck(checkId, { signer: signCtx.signer, signature_b64: b64 });
      toast.success('Firma salvata');
      setSignCtx(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Firma fallita'); }
  };

  const openPdf = async (withReport) => {
    setPdfBusy(true);
    // Scheda aperta SUBITO al click per non farsi bloccare dal popup blocker
    const win = window.open('', '_blank');
    try {
      if (dirty && canEdit) await save();
      const r = await complianceApi.checkPdf(checkId, withReport);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (win && !win.closed) win.location = url; else window.open(url, '_blank');
      setShowPdf(false);
    } catch {
      if (win && !win.closed) win.close();
      toast.error(withReport ? 'Generazione report AI non riuscita, riprova' : 'Errore generazione PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setCommentBusy(true);
    try {
      await complianceApi.addCheckComment(checkId, { text: comment.trim() });
      setComment('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aggiornamento non salvato');
    } finally {
      setCommentBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm('Eliminare definitivamente questo controllo dallo storico?')) return;
    try {
      await complianceApi.deleteCheck(checkId);
      toast.success('Controllo eliminato');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eliminazione fallita'); }
  };

  return (
    <>
      <Dialog open onOpenChange={v => { if (!v && !signCtx) onClose(); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="compliance-detail"
                       onInteractOutside={e => { if (signCtx) e.preventDefault(); }}
                       onEscapeKeyDown={e => { if (signCtx) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Indice di conformità — {chk.store_name || storeName}
              <Badge variant="outline" className={isFinal
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                {isFinal ? 'Definitivo' : 'Bozza'}
              </Badge>
            </DialogTitle>
            <DialogDescription>Valutatore: {chk.evaluator_name || '—'}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-2 bg-slate-900 text-white p-3" data-testid="compliance-check-summary">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Conformità</p>
              <p className={`text-xl font-bold tabular-nums ${percent >= 90 ? 'text-emerald-400' : percent >= 70 ? 'text-amber-400' : 'text-red-400'}`}
                 data-testid="compliance-check-percent">{percent}%</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Punteggio</p>
              <p className="text-xl font-bold tabular-nums">{total}<span className="text-sm text-slate-400"> / {maxTotal}</span></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Voci valutate</p>
              <p className="text-xl font-bold tabular-nums">{scored}<span className="text-sm text-slate-400"> / {allItems.length}</span></p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Data controllo</label>
              <Input type="date" value={date} onChange={e => { setDate(e.target.value); setDirty(true); }}
                     disabled={!canEdit} className="rounded-none mt-1 h-9" data-testid="compliance-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Referente negozio</label>
              <Input value={referente} onChange={e => { setReferente(e.target.value); setDirty(true); }}
                     disabled={!canEdit} maxLength={120} placeholder="Nome del referente presente"
                     className="rounded-none mt-1 h-9" data-testid="compliance-referente" />
            </div>
          </div>

          <div className="space-y-4">
            {(tpl.sections || []).map(sec => (
              <div key={sec.section_id} className="border border-slate-200" data-testid={`compliance-section-${sec.section_id}`}>
                <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-slate-700">{sec.title}</h3>
                  <span className="text-xs text-slate-500 tabular-nums">{secTotal(sec)} / {sec.items.length * scaleMax}</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {sec.items.map(it => (
                    <div key={it.item_id} className="flex flex-col items-start gap-1.5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:py-1.5">
                      <span className="text-sm text-slate-700">{it.label}</span>
                      <div className="flex gap-1.5 sm:gap-1">
                        {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => scaleMin + i).map(v => (
                          <button key={v} type="button" onClick={() => setScore(it.item_id, v)}
                                  disabled={!canEdit}
                                  className={`w-9 h-9 text-sm sm:w-7 sm:h-7 sm:text-xs border transition-colors tabular-nums ${scores[it.item_id] === v
                                    ? 'bg-slate-900 text-white border-slate-900'
                                    : 'bg-white text-slate-500 border-slate-300 hover:border-slate-500 disabled:opacity-40'}`}
                                  data-testid={`compliance-score-${it.item_id}-${v}`}>
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Note e piano d'azione</label>
            <Textarea value={notes} onChange={e => { setNotes(e.target.value); setDirty(true); }}
                      disabled={!canEdit} rows={3} placeholder="Criticità rilevate, azioni correttive concordate, scadenze…"
                      className="rounded-none mt-1 text-sm" data-testid="compliance-notes" />
          </div>

          <div className="border border-slate-200">
            <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
              <h3 className="font-heading text-xs font-bold uppercase tracking-wider text-slate-700">
                Aggiornamenti ({(chk.comments || []).length})
              </h3>
              <p className="text-[11px] text-slate-400">Cronologia con data e ora — si possono aggiungere anche a controllo definitivo.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {(chk.comments || []).map(c => (
                <div key={c.comment_id} className="px-3 py-2" data-testid={`compliance-comment-${c.comment_id}`}>
                  <p className="text-[11px] text-slate-400">
                    {new Date(c.at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {' · '}<span className="font-medium text-slate-500">{c.author_name}</span>
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.text}</p>
                </div>
              ))}
              <div className="px-3 py-2 flex gap-2 items-start">
                <Textarea value={comment} onChange={e => setComment(e.target.value)} rows={1} maxLength={2000}
                          placeholder="Aggiungi un aggiornamento…"
                          className="rounded-none text-sm flex-1 min-h-[38px]" data-testid="compliance-comment-input" />
                <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none shrink-0"
                        onClick={addComment} disabled={commentBusy || !comment.trim()}
                        data-testid="compliance-comment-add-btn">
                  {commentBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />} Aggiungi
                </Button>
              </div>
            </div>
          </div>

          <div className="flex gap-4 flex-wrap items-center border-t border-slate-200 pt-3">
            <SigBlock sig={chk.signatures?.evaluator} label="Firma valutatore" testId="compliance-sign-evaluator"
                      onSign={async () => { if (dirty && canEdit) await save(); setSignCtx({ signer: 'evaluator', label: 'Firma valutatore' }); }} />
            <SigBlock sig={chk.signatures?.referente} label="Firma referente" testId="compliance-sign-referente"
                      onSign={async () => { if (dirty && canEdit) await save(); setSignCtx({ signer: 'referente', label: `Firma referente${referente ? ` — ${referente}` : ''}` }); }} />
          </div>

          <DialogFooter className="flex-wrap gap-2 items-center">
            {user?.role === 'operations_manager' && (
              <Button variant="outline" size="sm" className="h-8 text-red-600 border-red-200 mr-auto"
                      onClick={doDelete} data-testid="compliance-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina
              </Button>
            )}
            {canEdit && (
              <span className="text-[11px] text-slate-400 mr-1" data-testid="compliance-autosave-status">
                {saving ? 'Salvataggio…' : dirty ? 'Modifiche in attesa…' : 'Salvato ✓'}
              </span>
            )}
            {canEdit && (
              <Button variant={dirty ? 'default' : 'outline'} size="sm" className="h-8 gap-1.5" onClick={save}
                      disabled={saving} data-testid="compliance-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salva
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={finalize} data-testid="compliance-finalize-btn">
                <CheckCheck className="w-3.5 h-3.5" /> Finalizza
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setShowPdf(true)} disabled={pdfBusy}
                    data-testid="compliance-pdf-btn">
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showPdf && (
        <Dialog open onOpenChange={v => { if (!v && !pdfBusy) setShowPdf(false); }}>
          <DialogContent className="max-w-sm" data-testid="compliance-pdf-dialog">
            <DialogHeader>
              <DialogTitle>Genera PDF</DialogTitle>
              <DialogDescription>Il report AI analizza criticità, punti solidi e suggerisce un piano d'azione.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start gap-2 rounded-none" disabled={pdfBusy}
                      onClick={() => openPdf(false)} data-testid="compliance-pdf-plain-btn">
                <FileDown className="w-4 h-4" /> Solo scheda
              </Button>
              <Button className="w-full justify-start gap-2 rounded-none" disabled={pdfBusy}
                      onClick={() => openPdf(true)} data-testid="compliance-pdf-ai-btn">
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                PDF + report analisi AI
              </Button>
              {pdfBusy && <p className="text-[11px] text-slate-400">Generazione in corso… la scheda si aprirà da sola, non chiudere.</p>}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {signCtx && (
        <SignaturePad title={signCtx.label} subtitle={`${chk.store_name || storeName} — indice di conformità`}
                      onSubmit={doSign} onCancel={() => setSignCtx(null)} />
      )}
    </>
  );
}
